import React, { useState, useEffect } from "react";
import Header from "../../../components/header";
import ModalComponent from "../../../components/Modals/modal";
import { useSelector, useDispatch } from "react-redux";
import {
  calculateEarningsBreakdown,
  createNewPayslip,
} from "../../../redux/Calculator/calculatorAction";
import Select from "react-select";
import { employeeDropDown } from "../../../redux/Calculator/calculatorAction";
import cogoToast from "cogo-toast";
import GoBack from "../../../components/goBack";

// Format number with commas and 2 decimal places
const formatCurrency = (value) => {
  return Number(value || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const PayslipCalculatorPageAddition = () => {
  const [activeTab, setActiveTab] = useState("payslip_details");
  const [loading, setLoading] = useState(false);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  // Editable inputs only - matching the design
  const [payrollDate, setPayrollDate] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeType, setEmployeeType] = useState("regular");
  const [isTaxable, setIsTaxable] = useState(true);
  const [editableInputs, setEditableInputs] = useState({
    monthlyGrossSalary: "",
    performanceBonus: "",
    overtimePay: "",
    arrearsRetroactivePay: "",
    voluntaryPensionContribution: "",
    loanRepayment: "",
    penaltyDisciplinaryFine: "",
    hmo: "",
    rentRelief: "",
    hours_worked: 0,
    hourly_pay: 0,
  });

  // Backend response data for earnings breakdown (will be populated from API)
  const [earningsData, setEarningsData] = useState(null);

  // Redux state management
  const dispatch = useDispatch();
  const { employees } = useSelector((state) => state.payslipCalculator);

  // Handle employee selection
  const handleEmployeeDropDown = (val) => {
    setSelectedEmployees(val || []);
  };

  // Handle editable input changes
  const handleEditableInputChange = (field, value) => {
    setEditableInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Reset to Get Breakdown when inputs change
    setCreatePaySlip(false);
    // Clear earnings data when inputs change
    setEarningsData(null);
  };

  useEffect(() => {
    setEmployeeLoading(true);
    dispatch(employeeDropDown()).finally(() => setEmployeeLoading(false));
  }, [dispatch]);

  const employeeTypeOptions = [
    { value: "regular", label: "Full Time" },
    { value: "contract", label: "Contract" },
  ];

  // Validation
  const isFormValid =
    payrollDate &&
    employeeType &&
    selectedEmployees.length > 0 &&
    editableInputs.monthlyGrossSalary;

  const isGetBreakdownValid = editableInputs.monthlyGrossSalary;

  const handleSaveChanges = async () => {
    if (!isFormValid) {
      cogoToast.error(
        "Please select payroll date, employee and enter monthly gross salary",
      );
      return;
    }

    setLoading(true);

    const payload = {
      date_of_entry: payrollDate,
      employees: selectedEmployees.map((emp) => emp.id),
      employee_type: employeeType,
      gross_payment: Number(editableInputs.monthlyGrossSalary || 0),
      is_taxable: isTaxable,
      additions: {
        bonus: Number(editableInputs.performanceBonus || 0),
        overtime: Number(editableInputs.overtimePay || 0),
        arrears: Number(editableInputs.arrearsRetroactivePay || 0),
      },
      deductions: {
        voluntary_pension: Number(
          editableInputs.voluntaryPensionContribution || 0,
        ),
        loan_repayment: Number(editableInputs.loanRepayment || 0),
        hmo: Number(editableInputs.hmo || 0),
        rent_relief: Number(editableInputs.rentRelief || 0),
        penalty: Number(editableInputs.penaltyDisciplinaryFine || 0),
      },
      ...(employeeType === "contract" && {
        hours_worked: Number(editableInputs.hours_worked || 0),
        hourly_pay: Number(editableInputs.hourly_pay || 0),
      }),
    };

    try {
      const res = await dispatch(createNewPayslip(payload));

      if (res.payload.message === "Payslip Creation Successful") {
        setPayrollDate("");
        setSelectedEmployees([]);
        setEmployeeType("regular");
        setEditableInputs({
          monthlyGrossSalary: "",
          performanceBonus: "",
          overtimePay: "",
          arrearsRetroactivePay: "",
          voluntaryPensionContribution: "",
          loanRepayment: "",
          penaltyDisciplinaryFine: "",
          hmo: "",
          rentRelief: "",
          hours_worked: 0,
          hourly_pay: 0,
        });
        setIsTaxable(true);
        setEarningsData({});
        setCreatePaySlip(false);
        setShowGenerateModal(false);
      }

      if (res?.payload?.status === "success") {
        cogoToast.success("Payslip created successfully!");
        // Clear all form data and state
      } else if (res?.error) {
        setShowGenerateModal(true);
      }
    } catch (error) {
      cogoToast.error("An error occurred while creating payslip");
      setShowGenerateModal(true);
    } finally {
      setLoading(false);
      setShowGenerateModal(false);
    }
  };

  // Modal for generation confirmation
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [createPaySlip, setCreatePaySlip] = useState(false);

  const handleGetBreakdown = async () => {
    if (!isGetBreakdownValid) {
      cogoToast.error("Please enter monthly gross salary to get breakdown");
      return;
    }

    setLoading(true);

    const payload = {
      monthly_gross_salary: Number(editableInputs.monthlyGrossSalary || 0),
      is_taxable: isTaxable,
      additions: {
        bonus: Number(editableInputs.performanceBonus || 0),
        overtime: Number(editableInputs.overtimePay || 0),
        arrears: Number(editableInputs.arrearsRetroactivePay || 0),
      },
      deductions: {
        voluntary_pension: Number(
          editableInputs.voluntaryPensionContribution || 0,
        ),
        hmo: Number(editableInputs.hmo || 0),
        loan_repayment: Number(editableInputs.loanRepayment || 0),
        rent_relief: Number(editableInputs.rentRelief || 0),
        penalty: Number(editableInputs.penaltyDisciplinaryFine || 0),
      },
      ...(employeeType === "contract" && {
        hours_worked: Number(editableInputs.hours_worked || 0),
        hourly_pay: Number(editableInputs.hourly_pay || 0),
      }),
    };

    try {
      const res = await dispatch(calculateEarningsBreakdown(payload));

      if (res?.payload) {
        setEarningsData(res.payload.data);
        setCreatePaySlip(true);
      } else if (res?.error) {
        cogoToast.error(res.error.message || "Failed to get breakdown");
      }
    } catch (error) {
      cogoToast.error("Failed to get breakdown");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Confirmation Modal */}
      <ModalComponent
        title="Create Payslip"
        show={showGenerateModal}
        showModal={() => setShowGenerateModal(false)}
      >
        <div className="space-y-4 mt-0 overflow-scroll">
          {/* Summary Details */}
          <div className="bg-[#F6F7F6] rounded-lg p-4 space-y-3">
            {/* Payroll Date */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#5F6D60] font-medium">
                Payroll Date:
              </span>
              <span className="text-sm font-semibold text-[#3E473F]">
                {new Date(payrollDate).toLocaleDateString("en-NG")}
              </span>
            </div>

            {/* Employee Type */}
            <div className="flex justify-between items-center border-t border-t-[#E1E6E1] pt-3">
              <span className="text-sm text-[#5F6D60] font-medium">
                Employee Type:
              </span>
              <span className="text-sm font-semibold text-[#3E473F] capitalize">
                {employeeType === "regular" ? "Full Time" : "Contract"}
              </span>
            </div>

            {/* Number of Employees */}
            <div className="flex justify-between items-center border-t border-t-[#E1E6E1] pt-3">
              <span className="text-sm text-[#5F6D60] font-medium">
                Number of Employees:
              </span>
              <span className="text-sm font-semibold text-[#3E473F]">
                {selectedEmployees.length}
              </span>
            </div>

            {/* Is Taxable */}
            <div className="flex justify-between items-center border-t border-t-[#E1E6E1] pt-3">
              <span className="text-sm text-[#5F6D60] font-medium">
                Is Taxable:
              </span>
              <span className="text-sm font-semibold text-[#3E473F]">
                {isTaxable ? "Yes" : "No"}
              </span>
            </div>

            {/* Employees List */}
            <div className="border-t border-t-[#E1E6E1] pt-3">
              <p className="text-sm text-[#5F6D60] font-medium mb-2">
                Employees:
              </p>
              <div className="h-full overflow-y-auto gap-0 flex flex-wrap items-center">
                {selectedEmployees.map((emp, idx) => (
                  <p
                    key={idx}
                    className="text-sm font-extrabold text-[#3E473F] pl-2 capitalize"
                  >
                    • {emp.first_name} {emp.last_name}
                  </p>
                ))}
              </div>
            </div>
          </div>

          {/* Key Calculations */}
          {earningsData && (
            <div className="bg-white border border-[#E1E6E1] rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-[#3E473F] mb-3">
                Payment Summary
              </p>

              <div className="flex justify-between items-center">
                <span className="text-sm text-[#5F6D60]">Gross Salary:</span>
                <span className="text-sm font-semibold text-[#3E473F]">
                  ₦{formatCurrency(earningsData?.total_gross_pay || 0)}
                </span>
              </div>

              <div className="flex justify-between items-center border-t border-t-[#E1E6E1] pt-3">
                <span className="text-sm text-[#5F6D60]">
                  Total Deductions:
                </span>
                <span className="text-sm font-semibold text-[#B92043]">
                  -₦
                  {formatCurrency(
                    earningsData?.monthly_deductions
                      ?.total_monthly_deductions || 0,
                  )}
                </span>
              </div>

              <div className="flex justify-between items-center border-t border-t-[#E1E6E1] pt-3 bg-[#F6F7F6] -mx-4 -mb-4 px-4 py-3 rounded-b-lg">
                <span className="text-sm font-semibold text-[#3E473F]">
                  Net Pay:
                </span>
                <span className="text-lg font-bold text-primary">
                  ₦{formatCurrency(earningsData?.net_pay?.final_net_pay || 0)}
                </span>
              </div>
            </div>
          )}

          {/* Warning Message */}
          <div className="bg-[#FFF5F5] border border-[#FFE0E0] rounded-lg p-3">
            <p className="text-xs text-[#B92043] font-medium">
              ⚠️ Please verify all details are correct. This action will create
              payslips for all selected employees.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-[24px] flex justify-end gap-[16px] items-center border-t border-t-[#E1E6E1]">
          <button
            onClick={() => setShowGenerateModal(false)}
            disabled={loading}
            className="text-sm font-medium leading-5 text-[#5C715E] hover:text-[#3E473F] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              handleSaveChanges();
            }}
            disabled={loading}
            className="outline-0 bg-primary px-5 py-2.5 rounded-[8px] h-[40px] text-sm font-medium leading-5 text-white disabled:bg-[#ABB6AC] hover:bg-[#1d5a21]"
          >
            {loading ? "Creating..." : "Confirm & Create"}
          </button>
        </div>
      </ModalComponent>
      <GoBack />

      <Header text="Create Payslip"></Header>

      <div className="mt-4">
        {/* Payroll Date & Employee Selection */}
        <div className="bg-white rounded-lg px-6 py-4 mb-4">
          <div className="max-w-full lg:max-w-[50%]">
            {/* ----Payroll Date----- */}
            <div>
              <label className="block text-sm font-medium text-[#5F6D60] mb-2">
                Payroll Date*
              </label>
              <input
                type="date"
                value={payrollDate}
                onChange={(e) => setPayrollDate(e.target.value)}
                className="w-full px-4 py-2.5 border border-[#96A397] rounded-lg outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {/* ----EMPLOYEE TYPE----- */}
            <div className="mt-4 text-seconday font-normal text-sm leading-[18px] mb-3.5">
              <label className="block mb-2 text-[#5F6D60]  text-sm font-medium">
                Employee Type*
              </label>
              <Select
                options={employeeTypeOptions}
                value={employeeTypeOptions.find(
                  (o) => o.value === employeeType,
                )}
                name="Employee Type"
                placeholder="Select Employee Type"
                onChange={(opt) => setEmployeeType(opt ? opt.value : "")}
                isSearchable={false}
                classNamePrefix="select_control"
                className="select_style"
              />
            </div>

            <div>
              {/* ----EMPLOYEE ----- */}
              <label className="block mb-2 text-[#5F6D60] font-medium text-sm ">
                Employee*
              </label>
              <Select
                onChange={handleEmployeeDropDown}
                options={employees}
                required={true}
                name="list of employees"
                placeholder="Select Employee(s)"
                className="select_style"
                isMulti
                isSearchable
                isLoading={employeeLoading}
                classNamePrefix="select_control"
                getOptionLabel={(options) =>
                  `${options.first_name} ${options.last_name}`
                }
                getOptionValue={(options) => options.id}
              ></Select>
            </div>
          </div>
        </div>

        {/* Payslip Details / History Tabs */}
        <div className="bg-white rounded-lg px-6 py-3  mb-4">
          <div className="flex gap-4 mb-0">
            <p
              className={
                activeTab === "payslip_details"
                  ? "font-semibold text-sm text-primary underline  underline-offset-4 decoration-2  cursor-pointer"
                  : "font-medium text-sm text-[#96A397] cursor-pointer hover:text-primary"
              }
              onClick={() => setActiveTab("payslip_details")}
            >
              Payslip Details
            </p>
            <p
              className={
                activeTab === "history"
                  ? "font-semibold text-sm text-primary underline underline-offset-4 decoration-2  cursor-pointer"
                  : "font-medium text-sm text-[#96A397] cursor-pointer hover:text-primary"
              }
              onClick={() => setActiveTab("history")}
            >
              History
            </p>
          </div>
        </div>

        {activeTab === "payslip_details" && (
          <div className="flex flex-col-reverse lg:flex-row gap-6">
            {/*--------------- Left Column - Earnings Breakdown (Read-only from Backend) --------------- */}
            <div className="w-full lg:w-[60%]">
              {/* Fixed Allowances */}
              <div className="bg-white rounded-lg mb-4">
                <div className=" border-b border-b-[#E1E6E1] py-3 px-3">
                  <h3 className="text-sm font-semibold text-[#3E473F]">
                    Earnings Breakdown
                  </h3>
                </div>
                <div className=" border-b border-b-[#E1E6E1] px-3 py-2 bg-[#F6F7F6]">
                  <p className="text-sm text-[#5F6D60] font-semibold">
                    Fixed Allowances (Gross Salary breakdown)
                  </p>
                </div>
                <div className="space-y-0">
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Basic Salary{" "}
                      <span className="text-[#5F6D60] text-xs">
                        {earningsData?.fixed_allowances?.basic_salary
                          ?.percentage || "20%"}
                      </span>
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.fixed_allowances?.basic_salary?.amount ||
                          0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Housing Allowance{" "}
                      <span className="text-[#5F6D60] text-xs">
                        {earningsData?.fixed_allowances?.housing_allowance
                          ?.percentage || "10%"}
                      </span>
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.fixed_allowances?.housing_allowance
                          ?.amount || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Entertainment{" "}
                      <span className="text-[#5F6D60] text-xs">
                        {earningsData?.fixed_allowances?.entertainment_allowance
                          ?.percentage || "15%"}
                      </span>
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.fixed_allowances?.entertainment_allowance
                          ?.amount || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Transport Allowance{" "}
                      <span className="text-[#5F6D60] text-xs">
                        {earningsData?.fixed_allowances?.transport_allowance
                          ?.percentage || "5%"}
                      </span>
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.fixed_allowances?.transport_allowance
                          ?.amount || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Leave Allowance{" "}
                      <span className="text-[#5F6D60] text-xs">
                        {earningsData?.fixed_allowances?.leave_allowance
                          ?.percentage || "20%"}
                      </span>
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.fixed_allowances?.leave_allowance
                          ?.amount || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Utility Allowance{" "}
                      <span className="text-[#5F6D60] text-xs">
                        {earningsData?.fixed_allowances?.utility_allowance
                          ?.percentage || "20%"}
                      </span>
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.fixed_allowances?.utility_allowance
                          ?.amount || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Training Allowance{" "}
                      <span className="text-[#5F6D60] text-xs">
                        {earningsData?.fixed_allowances?.training_allowance
                          ?.percentage || "10%"}
                      </span>
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.fixed_allowances?.training_allowance
                          ?.amount || 0,
                      )}
                    </span>
                  </div>
                </div>

                {/* Additions */}

                <div className=" border-b border-b-[#E1E6E1] px-3 py-2 bg-[#F6F7F6]">
                  <p className="text-sm text-[#5F6D60] font-semibold">
                    Additions
                  </p>
                </div>
                <div className="">
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-[7px]">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Performance Bonus
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.additions?.performance_bonus || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-[6.5px]">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Arrears
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦{formatCurrency(earningsData?.additions?.arrears || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm  px-3 py-[6.5px]">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Overtime
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦{formatCurrency(earningsData?.additions?.overtime || 0)}
                    </span>
                  </div>
                </div>

                {/* Total Gross Pay */}
                <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2 pt-3 border-t border-[#E1E6E1]">
                  <span className="text-[#3E473F] font-semibold text-base ">
                    Total Gross Pay
                  </span>
                  <span className="text-primary font-bold text-lg">
                    ₦{formatCurrency(earningsData?.total_gross_pay || 0)}
                  </span>
                </div>
              </div>

              {/* Tax Computation */}
              <div className="bg-white rounded-lg mb-4">
                <div className=" border-b py-3 px-3">
                  <h3 className="text-sm font-semibold text-tertiary">
                    Tax Computation (Annual)
                  </h3>
                </div>
                <div className=" border-b px-3 py-2 bg-[#F6F7F6]">
                  <p className="text-sm text-[#5F6D60] font-semibold">
                    Annual Reliefs & Exemptions
                  </p>
                </div>
                <div className="space-y-0">
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Annual Pension
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.tax_reliefs?.annual_pension_relief || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Annual HMO / NHIS
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.tax_reliefs?.annual_hmo_relief || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Annual Rent Relief
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      ₦
                      {formatCurrency(
                        earningsData?.tax_reliefs?.annual_rent_relief || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2 pt-2 ">
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      Total Annual Relief
                    </span>
                    <span className="text-[#3E473F] font-bold text-base ">
                      ₦
                      {formatCurrency(
                        earningsData?.tax_reliefs?.total_annual_relief || 0,
                      )}
                    </span>
                  </div>

                  {/* <div className=" border-b border-b-[#E1E6E1] px-3 py-2 bg-[#F6F7F6]">
                    <p className="text-sm text-[#5F6D60] font-semibold">
                      Additions
                    </p>
                  </div> */}
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2 bg-[#F6F7F6]">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      PAYE Tax Band (Annual)
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      {earningsData?.paye_calculation?.tax_band ||
                        "First ₦800,000 @ 0%"}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm  px-3 py-4 ">
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      Taxable Income (Annual)
                    </span>
                    <span className="text-[#3E473F] font-bold text-lg ">
                      ₦
                      {formatCurrency(
                        earningsData?.paye_calculation?.annual_taxable_income ||
                          0,
                      )}
                    </span>
                  </div>
                </div>

                {/* Monthly PAYE Tax */}
                <div className="flex justify-between text-sm px-3 py-3 border-t border-[#E1E6E1]">
                  <span className="text-[#3E473F]  text-base font-semibold">
                    Monthly PAYE Tax
                  </span>
                  <span className="text-[#B92020] text-lg font-bold">
                    ₦
                    {formatCurrency(
                      earningsData?.paye_calculation?.monthly_paye_tax || 0,
                    )}
                  </span>
                </div>
              </div>

              {/* Monthly Deductions */}
              <div className="bg-white rounded-lg mb-4">
                <div className=" border-b py-3 px-3">
                  <h3 className="text-sm font-semibold text-[#B92043]">
                    Monthly Deductions
                  </h3>
                </div>
                <div className="space-y-0">
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      PAYE Tax
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      -₦
                      {formatCurrency(
                        earningsData?.monthly_deductions?.paye_tax || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Pension Contribution
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      -₦
                      {formatCurrency(
                        earningsData?.monthly_deductions
                          ?.employee_pension_contribution || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-b-[#E1E6E1] px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      HMO / NHIS
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      -₦
                      {formatCurrency(
                        earningsData?.monthly_deductions?.hmo_nhis || 0,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm  px-3 py-2">
                    <span className="text-[#5F6D60] font-semibold text-sm">
                      Loan Repayment
                    </span>
                    <span className="text-[#3E473F] font-semibold text-sm ">
                      -₦
                      {formatCurrency(
                        earningsData?.monthly_deductions?.loan_repayment || 0,
                      )}
                    </span>
                  </div>
                </div>

                {/* Total Deductions */}
                <div className="flex justify-between text-sm px-3 py-3 border-t border-[#E1E6E1]">
                  <span className="text-[#5F6D60] font-semibold text-base ">
                    Total Monthly Deductions
                  </span>
                  <span className="text-[#B92043] text-lg font-bold">
                    -₦
                    {formatCurrency(
                      earningsData?.monthly_deductions
                        ?.total_monthly_deductions || 0,
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/*--------------- Right Column - Net Pay + Editable Inputs ---------------*/}
            <div className="w-full lg:w-[40%] ">
              {/*------------- Net Pay Card  -------------*/}
              <div className="bg-primary rounded-lg px-6 mb-4 flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-semibold  mb-1">
                    Net Pay (Final)
                  </p>
                  <p className="text-white text-2xl font-bold">
                    ₦{formatCurrency(earningsData?.net_pay?.final_net_pay || 0)}
                  </p>
                </div>
                <div className=" flex items-center justify-center w-[100px] xl:w-[140px] h-fit">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="146"
                    height="107"
                    viewBox="0 0 146 107"
                    fill="none"
                  >
                    <mask
                      id="mask0_4709_65750"
                      style={{ maskType: "alpha" }}
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="-20"
                      width="146"
                      height="147"
                    >
                      <rect
                        y="-19.0869"
                        width="145.174"
                        height="145.174"
                        fill="#D9D9D9"
                      />
                    </mask>
                    <g mask="url(#mask0_4709_65750)">
                      <path
                        d="M18.1486 101.891C14.8217 101.891 11.9737 100.706 9.60452 98.3372C7.23536 95.9681 6.05078 93.12 6.05078 89.7931V29.304C6.05078 27.5902 6.63047 26.1536 7.78984 24.9942C8.94922 23.8348 10.3858 23.2551 12.0997 23.2551C13.8135 23.2551 15.2502 23.8348 16.4095 24.9942C17.5689 26.1536 18.1486 27.5902 18.1486 29.304V89.7931H114.931C116.645 89.7931 118.082 90.3728 119.241 91.5322C120.4 92.6916 120.98 94.1282 120.98 95.842C120.98 97.5559 120.4 98.9925 119.241 100.152C118.082 101.311 116.645 101.891 114.931 101.891H18.1486ZM42.3442 77.6953C39.0173 77.6953 36.1693 76.5107 33.8002 74.1416C31.431 71.7724 30.2464 68.9244 30.2464 65.5975V17.2062C30.2464 13.8793 31.431 11.0313 33.8002 8.66213C36.1693 6.29298 39.0173 5.1084 42.3442 5.1084H127.029C130.356 5.1084 133.204 6.29298 135.573 8.66213C137.942 11.0313 139.127 13.8793 139.127 17.2062V65.5975C139.127 68.9244 137.942 71.7724 135.573 74.1416C133.204 76.5107 130.356 77.6953 127.029 77.6953H42.3442ZM54.4421 65.5975C54.4421 62.2706 53.2575 59.4226 50.8883 57.0534C48.5192 54.6843 45.6711 53.4997 42.3442 53.4997V65.5975H54.4421ZM114.931 65.5975H127.029V53.4997C123.702 53.4997 120.854 54.6843 118.485 57.0534C116.116 59.4226 114.931 62.2706 114.931 65.5975ZM84.6866 59.5486C89.7274 59.5486 94.012 57.7843 97.5405 54.2558C101.069 50.7273 102.833 46.4426 102.833 41.4019C102.833 36.3611 101.069 32.0765 97.5405 28.5479C94.012 25.0194 89.7274 23.2551 84.6866 23.2551C79.6458 23.2551 75.3612 25.0194 71.8327 28.5479C68.3041 32.0765 66.5399 36.3611 66.5399 41.4019C66.5399 46.4426 68.3041 50.7273 71.8327 54.2558C75.3612 57.7843 79.6458 59.5486 84.6866 59.5486ZM42.3442 29.304C45.6711 29.304 48.5192 28.1195 50.8883 25.7503C53.2575 23.3811 54.4421 20.5331 54.4421 17.2062H42.3442V29.304ZM127.029 29.304V17.2062H114.931C114.931 20.5331 116.116 23.3811 118.485 25.7503C120.854 28.1195 123.702 29.304 127.029 29.304Z"
                        fill="#D5A407"
                      />
                    </g>
                  </svg>
                </div>
              </div>

              {/* Editable Inputs */}
              <div className="bg-white rounded-lg">
                <div className=" border-b border-b-[#E1E6E1] py-3 px-3">
                  <h3 className="text-sm font-semibold text-[#3E473F]">
                    Editable Inputs
                  </h3>
                </div>

                <div className="">
                  {/* Monthly Gross Salary */}
                  <div className="mb-2 py-4 px-4">
                    <label className="block text-sm text-[5F6D60] font-medium mb-2">
                      Monthly Gross Salary*
                    </label>
                    <input
                      type="string"
                      placeholder="₦"
                      value={editableInputs.monthlyGrossSalary}
                      onChange={(e) =>
                        handleEditableInputChange(
                          "monthlyGrossSalary",
                          e.target.value,
                        )
                      }
                      className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                    />
                    <p className="text-sm font-bold text-[#5F6D60] mt-1">
                      ₦
                      {Number(
                        editableInputs?.monthlyGrossSalary || 0,
                      ).toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>

                  {/* Performance Bonus & Overtime Pay */}
                  <div className="grid grid-cols-2 gap-4 mb-4 px-4">
                    <div>
                      <label className="block text-sm text-[#5F6D60] font-medium mb-2">
                        Performance Bonus
                      </label>
                      <input
                        type="string"
                        placeholder="₦"
                        value={editableInputs.performanceBonus}
                        onChange={(e) =>
                          handleEditableInputChange(
                            "performanceBonus",
                            e.target.value,
                          )
                        }
                        className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                      />
                      {editableInputs?.performanceBonus && (
                        <p className="text-sm font-bold text-[#5F6D60] mt-1">
                          ₦
                          {Number(
                            editableInputs?.performanceBonus || 0,
                          ).toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm text-[5F6D60] font-medium mb-2">
                        Overtime Pay
                      </label>
                      <input
                        type="string"
                        placeholder="₦"
                        value={editableInputs.overtimePay}
                        onChange={(e) =>
                          handleEditableInputChange(
                            "overtimePay",
                            e.target.value,
                          )
                        }
                        className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                      />
                      {editableInputs?.overtimePay && (
                        <p className="text-sm font-bold text-[#5F6D60] mt-1">
                          ₦
                          {Number(
                            editableInputs?.overtimePay || 0,
                          ).toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Arrears / Retroactive Pay */}
                  <div className="mb-4 px-4">
                    <label className="block  text-[5F6D60] font-medium text-sm mb-2">
                      Arrears / Retroactive Pay
                    </label>
                    <input
                      type="string"
                      placeholder="₦"
                      value={editableInputs.arrearsRetroactivePay}
                      onChange={(e) =>
                        handleEditableInputChange(
                          "arrearsRetroactivePay",
                          e.target.value,
                        )
                      }
                      className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                    />
                    {editableInputs?.arrearsRetroactivePay && (
                      <p className="text-sm font-bold text-[#5F6D60] mt-1">
                        ₦
                        {Number(
                          editableInputs?.arrearsRetroactivePay || 0,
                        ).toLocaleString("en-NG", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    )}
                  </div>

                  {employeeType === "contract" && (
                    <>
                      {/* Contract Section */}
                      <div className=" border-y border-y-[#E1E6E1] py-3 px-3 bg-[#F6F7F6]">
                        <h3 className="text-sm font-semibold text-[#3E473F]">
                          Contract
                        </h3>
                      </div>

                      {/* Hours Worked & Hourly Pay */}
                      <div className="grid grid-cols-2 gap-4 mb-4 px-4 mt-3">
                        <div>
                          <label className="block text-sm text-[5F6D60] font-medium mb-2">
                            Hours Worked
                          </label>
                          <input
                            type="string"
                            placeholder="160"
                            value={editableInputs.hours_worked}
                            onChange={(e) =>
                              handleEditableInputChange(
                                "hours_worked",
                                e.target.value,
                              )
                            }
                            className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-[5F6D60] font-medium mb-2">
                            Hourly Pay
                          </label>
                          <input
                            type="string"
                            placeholder="₦"
                            value={editableInputs.hourly_pay}
                            onChange={(e) =>
                              handleEditableInputChange(
                                "hourly_pay",
                                e.target.value,
                              )
                            }
                            className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                          />
                          {editableInputs?.hourly_pay && (
                            <p className="text-sm font-bold text-[#5F6D60] mt-1">
                              ₦
                              {Number(
                                editableInputs?.hourly_pay || 0,
                              ).toLocaleString("en-NG", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              per hour
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Deductions Section */}

                  <div className=" border-y border-y-[#E1E6E1] py-3 px-3 bg-[#F6F7F6]">
                    <h3 className="text-sm font-semibold text-[#3E473F]">
                      Deductions
                    </h3>
                  </div>

                  {/* Voluntary Pension Contribution */}
                  <div className="mb-4 px-4 mt-3">
                    <label className="block  text-[5F6D60] font-medium text-sm mb-2">
                      Voluntary Pension Contribution
                    </label>
                    <input
                      type="string"
                      placeholder="₦"
                      value={editableInputs.voluntaryPensionContribution}
                      onChange={(e) =>
                        handleEditableInputChange(
                          "voluntaryPensionContribution",
                          e.target.value,
                        )
                      }
                      className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                    />
                    {editableInputs?.voluntaryPensionContribution && (
                      <p className="text-sm font-bold text-[#5F6D60] mt-1">
                        -₦
                        {Number(
                          editableInputs?.voluntaryPensionContribution || 0,
                        ).toLocaleString("en-NG", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    )}
                  </div>

                  {/* Loan Repayment */}
                  <div className="mb-4 px-4">
                    <label className="block text-sm text-[5F6D60] font-medium mb-2">
                      Loan Repayment
                    </label>
                    <input
                      type="string"
                      placeholder="₦"
                      value={editableInputs.loanRepayment}
                      onChange={(e) =>
                        handleEditableInputChange(
                          "loanRepayment",
                          e.target.value,
                        )
                      }
                      className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                    />
                    {editableInputs?.loanRepayment && (
                      <p className="text-sm font-bold text-[#5F6D60] mt-1">
                        -₦
                        {Number(
                          editableInputs?.loanRepayment || 0,
                        ).toLocaleString("en-NG", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    )}
                  </div>

                  {/* Penalty / Disciplinary Fine */}
                  <div className="mb-4 px-4">
                    <label className="block  text-[5F6D60] font-medium text-sm mb-2">
                      Penalty / Disciplinary Fine
                    </label>
                    <input
                      type="string"
                      placeholder="₦"
                      value={editableInputs.penaltyDisciplinaryFine}
                      onChange={(e) =>
                        handleEditableInputChange(
                          "penaltyDisciplinaryFine",
                          e.target.value,
                        )
                      }
                      className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                    />
                    {editableInputs?.penaltyDisciplinaryFine && (
                      <p className="text-sm font-bold text-[#5F6D60] mt-1">
                        -₦
                        {Number(
                          editableInputs?.penaltyDisciplinaryFine || 0,
                        ).toLocaleString("en-NG", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    )}
                  </div>

                  {/* Is Taxable Checkbox */}
                  <div className="px-4 py-3 flex items-center gap-3 mb-4">
                    <input
                      id="isTaxable"
                      type="checkbox"
                      checked={isTaxable}
                      onChange={(e) => setIsTaxable(e.target.checked)}
                      className="h-4 w-4 rounded accent-primary"
                    />
                    <label
                      htmlFor="isTaxable"
                      className="text-sm text-[#5F6D60] font-medium"
                    >
                      Is Taxable
                    </label>
                  </div>

                  <div className=" border-y border-y-[#E1E6E1] py-3 px-3 bg-[#F6F7F6]">
                    <h3 className="text-sm font-semibold text-[#3E473F]">
                      Relief
                    </h3>
                  </div>

                  {/* HMO / NHIS */}
                  <div className="mb-4 px-4">
                    <label className="block  text-[5F6D60] font-medium text-sm mb-2 mt-3">
                      HMO / NHIS
                    </label>
                    <input
                      type="string"
                      placeholder="₦"
                      value={editableInputs.hmo}
                      onChange={(e) =>
                        handleEditableInputChange("hmo", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                    />
                    {editableInputs?.hmo && (
                      <p className="text-sm font-bold text-[#5F6D60] mt-1">
                        ₦
                        {Number(editableInputs?.hmo || 0).toLocaleString(
                          "en-NG",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </p>
                    )}
                  </div>

                  {/* Rent Relief */}
                  <div className="mb-4 px-4">
                    <label className="block text-sm text-[5F6D60] font-medium mb-2">
                      Rent Relief
                    </label>
                    <input
                      type="string"
                      placeholder="₦"
                      value={editableInputs.rentRelief}
                      onChange={(e) =>
                        handleEditableInputChange("rentRelief", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-[#E1E6E1] rounded-lg outline-none focus:border-primary"
                    />
                    {editableInputs?.rentRelief && (
                      <p className="text-sm font-bold text-[#5F6D60] mt-1">
                        ₦
                        {Number(editableInputs?.rentRelief || 0).toLocaleString(
                          "en-NG",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </p>
                    )}
                  </div>

                  <div className="px-4 pb-6 pt-2">
                    {/* Save Changes Button */}
                    {!createPaySlip && (
                      <button
                        onClick={handleGetBreakdown}
                        disabled={!isGetBreakdownValid || loading}
                        className="w-full py-3 bg-primary text-white text-sm font-semibold rounded-lg disabled:bg-[#ABB6AC] hover:bg-[#1d5a21]"
                      >
                        {loading ? "Getting breakdown..." : "Get Breakdown"}
                      </button>
                    )}

                    {createPaySlip && (
                      <button
                        disabled={loading || !isFormValid}
                        onClick={() => setShowGenerateModal(true)}
                        className="w-full py-3 bg-primary text-white text-sm font-semibold rounded-lg disabled:bg-[#ABB6AC] hover:bg-[#1d5a21]"
                      >
                        {loading ? "Creating payslip..." : "Create Payslip"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <div className="bg-white rounded-lg px-6 py-6">
            <p className="text-[#96A397] text-center py-10">
              No payslip history available
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default PayslipCalculatorPageAddition;
